import { or, and, not, setup } from 'xstate';

import { routePath } from 'config/route-config';
import { actionUrl } from 'config/static-content';
import { events } from 'machines/config/events';
import { guards } from 'machines/config/guards';
import { stringToBool } from 'utils/misc';

import type { TContext, TEvent } from 'machines/types/machine-config';

export const directSalesMachineConfig = {
  id: 'directSalesMachine',
  context: {
    guards: {}
  },
  initial: 'home',
  states: {
    home: {
      on: {
        NEXT: [
          {
            target: 'meterpointValidation',
            guard: 'isUpsellEnabledMeterValidationRequired'
          },
          {
            target: 'energyNeeds',
            description: `or(['hasAnyMeterValidationRequired', 'isUpsellEnabled'])`,
            guard: or(['hasAnyMeterValidationRequired', 'isUpsellEnabled'])
          },
          {
            target: 'selectOptions',
            description: `or(['hasMeterExceptions', 'hasMixedMeters'])`,
            guard: or(['hasMeterExceptions', 'hasMixedMeters'])
          },
          {
            target: 'energyNeeds'
          }
        ],

        BLOCK_JOURNEY: {
          target: 'businessUpdatesAnnouncement'
        }
      },
      meta: {
        path: routePath.home
      }
    },

    businessUpdatesAnnouncement: {
      on: {
        ALLOW_JOURNEY: {
          target: 'home'
        }
      },

      meta: {
        path: routePath.businessUpdatesAnnouncement
      }
    },

    meterpointValidation: {
      on: {
        NEXT: [
          {
            target: 'selectOptions',
            description: `or(['hasMeterExceptions', 'hasMixedMeters'])`,
            guard: or(['hasMeterExceptions', 'hasMixedMeters'])
          },
          {
            target: 'energyNeeds'
          }
        ],
        GUIDE_CLICK: {
          target: 'meterPointGuide'
        }
      },
      meta: {
        path: routePath.meterpointValidation
      }
    },

    meterPointGuide: {
      meta: {
        path: routePath.meterpointGuide
      }
    },

    selectOptions: {
      on: {
        ELIGIBLE_FUEL: {
          target: 'energyNeeds'
        },
        EDIT_ADDRESS: {
          target: 'home'
        },
        HOME_MOVE: [
          {
            target: 'externalContactUs',
            description: `or([
              'isMixedNepSap',
              and([not('allowJourneyMoveIn'), 'isBGECustomer'])
            ])`,

            guard: or([
              'isMixedNepSap',
              and([not('allowJourneyMoveIn'), 'isBGECustomer'])
            ])
          },
          {
            target: 'externalMoveIn',
            guard: 'isBGECustomer'
          },
          {
            target: 'externalBgClassicHomeMove'
          }
        ],
        MIXED_METERS: {
          target: 'energyNeeds'
        }
      },
      meta: {
        path: routePath.selectOptions
      }
    },

    energyNeeds: {
      on: {
        NEXT: [
          {
            target: 'meterpointValidation',
            guard: 'meterValidationRequired'
          },
          {
            target: 'selectOptions',
            description: `or(['hasMeterExceptions', 'hasMixedMeters'])`,
            guard: or(['hasMeterExceptions', 'hasMixedMeters'])
          },
          {
            target: 'evTariffInformation',
            guard: 'showEvTariffInformation'
          },
          {
            target: 'emailCapture',
            guard: 'allowAbandonedCart'
          },
          {
            target: 'quote'
          }
        ]
      },
      meta: {
        path: routePath.energyNeeds
      }
    },

    evTariffInformation: {
      on: {
        NEXT: [
          {
            target: 'emailCapture',
            guard: 'allowAbandonedCart'
          },
          {
            target: 'quote'
          }
        ]
      },
      meta: {
        path: routePath.evTariffInformation
      }
    },

    emailCapture: {
      on: {
        NEXT: [
          {
            target: 'quote'
          }
        ]
      },
      meta: {
        path: routePath.emailCapture
      }
    },

    quote: {
      on: {
        NEXT: [
          {
            target: 'peakSave',
            guard: 'hasElectricityTOUMeter'
          },
          {
            target: 'personalDetails'
          }
        ],
        EMAIL_QUOTE: {
          target: 'emailQuote'
        },
        CONSUMPTION: {
          target: 'energyNeeds'
        }
      },
      meta: {
        path: routePath.quote
      }
    },

    peakSave: {
      on: {
        NEXT: {
          target: 'personalDetails'
        }
      },
      meta: {
        path: routePath.peakSave
      }
    },

    emailQuote: {
      on: {
        NEXT: {
          target: 'quote'
        }
      },
      meta: {
        path: routePath.emailQuote
      }
    },

    personalDetails: {
      on: {
        NEXT: [
          {
            target: 'priorityServicesRegister',
            guard: 'hasPSR'
          },
          {
            target: 'directDebit',
            guard: 'isAnyDirectDebit'
          },
          {
            target: 'orderSummary'
          }
        ]
      },
      meta: {
        path: routePath.personalDetails
      }
    },

    priorityServicesRegister: {
      on: {
        NEXT: [
          {
            target: 'directDebit',
            guard: 'isAnyDirectDebit'
          },
          {
            target: 'orderSummary'
          }
        ]
      },
      meta: {
        path: routePath.psrEntry
      }
    },

    directDebit: {
      on: {
        NEXT: {
          target: 'orderSummary'
        }
      },

      meta: {
        path: routePath.directDebit
      }
    },

    orderSummary: {
      on: {
        NEXT: {
          target: 'orderComplete'
        },
        CHANGE_PERSONAL_DETAILS: {
          target: 'personalDetails'
        },
        CHANGE_ADDRESS: {
          target: 'home'
        },
        CHANGE_PAYMENT_DETAILS: {
          target: 'directDebit'
        },
        CHANGE_PSR_DETAILS: {
          target: 'priorityServicesRegister'
        },
        CHANGE_ENERGY_NEEDS: {
          target: 'energyNeeds'
        }
      },
      meta: {
        path: routePath.orderSummary
      }
    },

    orderComplete: {
      on: {
        SECOND_QUOTE: {
          target: 'externalSecondQuote'
        }
      },
      meta: {
        path: routePath.orderComplete
      }
    },

    externalBgSalesApp: {
      meta: {
        path: actionUrl.bgSalesApp
      }
    },

    externalContactUs: {
      meta: { path: actionUrl.contact }
    },
    externalBgClassicHomeMove: {
      meta: { path: actionUrl.bgClassicHomeMoveUrl }
    },

    externalMoveIn: {
      meta: { path: actionUrl.moveIn }
    },

    externalSecondQuote: {
      meta: { path: actionUrl.secondQuote }
    }
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ...(events as any)
};

const { VITE_XSTATE_DEBUG } = import.meta.env;
const debug = stringToBool(VITE_XSTATE_DEBUG);
// for VS extesnion
/* istanbul ignore next -- @preserve */
debug &&
  setup({
    types: {} as {
      context: TContext;
      events: TEvent;
    },
    guards
  }).createMachine({
    /** @xstate-layout N4IgpgJg5mDOIC5QQJYCcwGMAuBlAhgDZwCy+mAFigHZgB0FA9gLZgDEAcgKIAaAKgG0ADAF1EoAA6NYKbCkbVxIAB6IAjGoBMQugDYAnAA4hugKxGzAdk2GANCACe6gMybTdIZrVCALIcu6bqa6hgC+ofaoGDgExLBklDT0TKycvIJqYkggUjJyCkqqCBraekYm5oZWNvZOxaY+anQ++vq6vroths7OauGR6Fh4RKTkVLQMLOzc-AKaWZLSsvKK2UUlOgbGZham1naOiPq9dJrOQnv6JvrWrf0gUUOxo4kTKdPpAs4LOUv5q6B1lpNuUdlU9jVDsVOpo9GpDPo1Lo1KY1K01JZ7o8YiN4mMkpNUgAhAAyAHkAMIAaQA+gApMkAVQAStwAJrCH65ZYFNbqYFlbaVaoHOoGHTeZytYzGfSmZxYwY4uIJcb0ABGAFcZLRYLBGRIIPhsHAAILUaiMTXUTBgVjUbBpWaiJTc-6FdSGJF0SwIhFXaxqc6mWqIEI+OimM4+ToNcGGTSK6LDFX4iZanVwfWG41mi1Wm12sAOtimknkgDq9KZrK4HJd2TdKw9xVcOg6vkM3U0+h8zh8oYQuildBugd0ln7vjcSaeuNVBIzST1BqNJtguE16uYsi4zHwKEITsEDcWeWbfIQmhjEdM8p6umHzhDUPDkejsb8ZgTs+VLzVdCsCaaBSDQ2AAGpECga4rMenKun8F6Ap63q+kYrRCIGwaDt4E6Rj4Qj6Jo2hRho-i-im-4EkBYAgYwYGQYQ0HGrBMwZFyiG8shxReroPp+hhWEXDhrjuFUxxdoiliWDGYQRA8SqUXirz0DRdEMVBMEKGwADijIAJIACJcDSFIkvp1LwY2nEAioKF8Wh-qYV42FQsCfGBAEXZqI0lhtIm8nYkpC4TLAYDEDgZISP8sBsFw5k6fppImQAYoy8VWWePK2UU16dJG97OI+PQvnU3iTgVXa9DG5T9hRzzKQBYURdgUUxXFhn6XwNKmoZhnMlwuC4JlvznlxdnFDc+h0F2lieHKRFFZOg5epsE42IYezyo0Pj1fOab0M1QxtSssUABJkiQJkkGS4FcCNTbjblN4FT0RVPqVRyeDNPlEd0D4In0gWKQ1IWHeFx3RadbAXVdNI3XdAiZAhY05YgeW3oVxXPjhnjOM0rhuJ23iaJ0e2pipdBHZFUMKOdl3Xbd93zCj2Uthjr0Ph9K1RnoVU+JOPgtFo5NUaFEM0+1JD6TwXCGfDXB8FwzLDaeo1s5eHN3m92OfcUWj4yEDRaD4pibcYpii41BLFrRUAOBwYCQLFbEPTZ7MNIbMp9ucQhCEVOFC3xfak35jQufKVtg3QttoPbjvO3ByPWajHtCIYdBFQRrhysEnQ4Ri+Nel4EkXMLUcHTHtBxw7TsQC7nwsynGvcW4WgzURc2WBod7mHrXjXjN1iYcXcoxpbwPJqDlex-HdcN7M3ys+6l6iZYejtP5Nx+76zg4VU69m0LvsBBibQV5Ts+14nrs+Bxqea4EGc9nK5y+oRZs4QE+PwsRJidMObYF8AJgAAG58HwGgFAAAzaB+lqDQMYGgfc-w4Jq0emjK8fZmjdHmkiBo1hpKDmIqbAm8oQiBClM+YBNtwGQJgXAhBSCUGsU+MnLKK9W7YL8OcHs+CBaaCIW5WMdAyKImRKiVoAUBhT32pffch4KT4GipqDAaD74twmv2PyzRPBzRaKTLO-c-YRnOP4fww5UR7DkjIucFMAIAEdNSMBNOo5eSEJpWFOBCKobgrgmFFPyCMPgvBeghF4cO0iFKyPsQSJxLiPizHYerThni9jeP2IEcwfsQjEP7KOTa5wrAxmHEGGhEx4muK4CQU0+kSQ0gAIqMjJErN2D9uJeLcJkvxOTAnQhCXCQGEi0REXKfQSp7AKRkg4LgRkJAAAKfB9LTLaZooonSfFZP8bktyVUfSmxaN3AiAR9BjLoBIMA+AADWBBQGJJPBo1JQJShbAqLsfYBdgmhJRNYCJWgolBWnvIg8hAGnONca7dB7tLzgh9MiY4LR2g9xwttDwQcgzlHTl2M5Fy0CwAUEQQyYBsAgoXg89xT17L8XQgGFywkoRekzm0f2NxLiYV0Di2i+LqCEuJaSpOjyPHrF4tSpyQl+5tBmvKLQlRnAykxJPOxYt6C4u5byklh4yVzEFZSniqEBK0qDPSuoZx8ZmxhJ0OUI8J62L-NbCYEgoFINkA4XAtFQEoFtLAZkYAoAoFgMBNxzcnmIDXhvQigRt4jz3lCN675SbaBko+TaQNbXBUro6+QUDsCuvdZ6uAPq-UBtogKilmCE3P1aPKEeH8+k9nxkIJEk4jDSRsNQxVdro5BSJeqWQQaOFCtDQLaaBFBG+CIsOWMg4iJMvaFKSwbKAhnKQRAWim5mD7jQA4ftKTB1YPytrLmJVBxUNOHNEmA9t7kQ7emymK612ag3ZA7dFIzqmg4DpEy8zla4GmWWGkxk+C1JJKrHVmD4RzVETYdowQ5T+CRPvfGpsjCm1MCYc4PkOU3qBQBe9aB12bpfW+j9Jler9UGqBstLY0R+Ezph4iZsoxYpPTGUcfk5oGGsIxhdy60Crvw4+wjbBX3vs-TSeZpo2RXQ4N1QDwHKPBr3VrLG3MoQD1HOYHongow9k6AqtNOGCR4YI8+4TxGxPzNwMyADit5OrJDfuzGOtVN1H7D-H22gqjtBgza6JSr7X0GM4J0zImSM0i4NwZkOk2Q0m4HLBTA7dXKec8eqEx9Z0hDbBiYdvH+MUhYBIYgrjcBcCmRweWTSWn3She0rRXTw1bzmtGk9xwz1+08P7KUvh23yUtKu+A2RAVyLVFRy8ABaXQg4JseHa32J+bQrjYuw8Ngk7xRvcXRGiiSW0wluGnXK5ovYza+jzvCM5S5dTZjXHmS01pbT2mwOtiaKIUSiNEgYEZeVBxvkMKbFEQsYxIkEed7Uy4ru5g3FuHc2A9wgqe7lQuegKjd0bSYFy33fszT+w0dFnkQeZhXDmdcm5ty7gUYQTcmAvUDcS+Woi7h4SYU7L2f7GOIy-asQD5EePluxImGpUCDpGLMVSRg6jlxdG9n9iE9rDQRJuEziEwBfhCJHbOWpeZ9EHQ6U1NBMA8P0aI+83sbwuF0duW0HxXsndfTtGjGc6mrVabUBp7upL15149AFlGOdcHcYVSMF2ToGL4QojOVfBO9cDdXmONNcxk4ejiW6PvPCVxkQjMnJl05vPlUxzoVA2B8DEHIJYuNMXl5SFoY0FoEI3RET9n7jK0REkDDHC98DnPAWY7k6USojA0eiquEO+0YwRVPBaGIWcPiQYAH+3Ts+PYZyJnR-IaIzeITJFzRjWVERQsTD75aIigFIMVsOsuTc-Adzo9ynXoIo5BgbBWL6RoCMMkx+XHg9eXzQ2+f0DtCCsFBJa-eUAqbwEJDQNCPWJEZwT3doZyVEYeHyTlPFAlQgIlDVQgV3cvbiFEXsPmFoMfQfeUSwSfDOC4KaTyNsEfHFJ1bNXNNAD1L1Qtf1YCaPHsFoSMTCHyNwfwIiHyE9HoLHPsXCIWeEc+TvLtRSHtWQAfYcWERFdOf2O3CfKEO8WEKfK4Z8Y4TodtAzU-QLPjB9J9LdNg68WEScbueEKUfwKhAQ4OYMNoE3ToYwXaCQyuPDfLZgQrYlfXRTXVE+U4DEABNwR8NoPpU9cwM+LwMAhdXQvzTtGeZQYCHlQgIkKABqU0CQCQaPEJYhKwvQLyCcaqKMfTeI29EBJI2iFI-LB0cgbARkLA6FVuenURYwfRBERoOXKECSTOL0QPVaAWIqcPSotAFItIikQgfAPUT1M6KYEgRgK-PwunBXRndolnLouoQpDwU3KUHyfwefYY5IogeYu5eBNglo1Y5nTogcNyPGH0eUKXGAwpdOQ4qoogN1TABQCAQAk0WQofBQ0fZQzQPI6SPQatY7YIA5Vw8IIAA */
    ...directSalesMachineConfig
  });
